import { rangeLabel } from './fileLabels';
import type { DraftSplit } from './pageSplitPrefill';
import type { GtPageSplit } from './types';

/** A split drawn on the PDF; pages are 1-based and inclusive. */
export interface SplitBand {
    /** 1-based position among the drawn splits, in page order. */
    number: number;
    first: number;
    last: number;
    docType: string;
    /** The type as shown to people, e.g. "Work Order (WO)". */
    typeLabel: string;
}

/** Turns a doc type value into the text shown for it. */
export type TypeLabeller = (docType: string) => string;

const rawTypeLabel: TypeLabeller = (docType) => docType.toUpperCase();

/** Split colours cycled by split number. */
const SPLIT_COLORS = ['#32ACAC', '#3B82F6', '#FD8535', '#8B5CF6', '#DB2777', '#0EA5E9', '#65A30D', '#B45309'];

/** Colour a split is drawn in, from its 1-based number. */
export function splitColor(number: number): string {
    return SPLIT_COLORS[(number - 1) % SPLIT_COLORS.length];
}

function numbered(ranges: Omit<SplitBand, 'number'>[]): SplitBand[] {
    return [...ranges]
        .sort((a, b) => a.first - b.first)
        .map((range, index) => ({ ...range, number: index + 1 }));
}

/** Bands from saved splits (0-based pages). */
export function bandsFromSaved(splits: GtPageSplit[], typeLabel: TypeLabeller = rawTypeLabel): SplitBand[] {
    return numbered(
        splits.map((split) => ({
            first: split.startPage + 1,
            last: split.endPage + 1,
            docType: split.docType,
            typeLabel: typeLabel(split.docType),
        })),
    );
}

/** Bands from the editor's unsaved rows (1-based text); rows with an invalid range draw no band. */
export function bandsFromDrafts(
    drafts: DraftSplit[],
    maxPage: number | null,
    typeLabel: TypeLabeller = rawTypeLabel,
): SplitBand[] {
    const valid = drafts.flatMap((draft) => {
        const start = draft.startPage.trim();
        const end = draft.endPage.trim();
        if (!/^\d+$/.test(start) || !/^\d+$/.test(end)) return [];
        const first = Number(start);
        const last = Number(end);
        if (first < 1 || last < first || (maxPage !== null && last > maxPage)) return [];
        return [{ first, last, docType: draft.docType, typeLabel: typeLabel(draft.docType) }];
    });
    return numbered(valid);
}

export function bandLabel(band: SplitBand): string {
    const type = band.docType === '' ? '' : ` · ${band.typeLabel}`;
    return `${rangeLabel(band.first, band.last)}${type}`;
}

/** The band each page falls in; where bands overlap, the earlier one wins. */
export function bandByPage(bands: readonly SplitBand[]): Map<number, SplitBand> {
    const byPage = new Map<number, SplitBand>();
    bands.forEach((band) => {
        for (let page = band.first; page <= band.last; page++) {
            if (!byPage.has(page)) byPage.set(page, band);
        }
    });
    return byPage;
}

/** Every band each page falls in, in band order; a page in two bands is in two splits. */
export function bandsOnPage(bands: readonly SplitBand[]): Map<number, SplitBand[]> {
    const byPage = new Map<number, SplitBand[]>();
    bands.forEach((band) => {
        for (let page = band.first; page <= band.last; page++) {
            byPage.set(page, [...(byPage.get(page) ?? []), band]);
        }
    });
    return byPage;
}
